import brandLogo from "@/assets/hustlemate-logo.png";
export { canvasBlob, downloadBlob } from "./flexCanvas";

export const FLEX_LAYERS = { brand: "Logo & brand", identity: "Username & date", icon: "Category icon", title: "Achievement title", metrics: "Achievement numbers", timeline: "Focus / break timeline" };
export const PROVENANCE = { solo: "○ SOLO · SELF-REPORTED", partner: "◈ PARTNER ROOM · UNCONFIRMED", verified: "✓ PARTNER VERIFIED", awarded: "★ PARTNER AWARDED" };
export function iconVariantForProvenance(provenance) {
  return provenance === "verified" || provenance === "awarded" ? "verified" : "solo";
}
export function flexCategoryIconUrl(category, provenance) {
  const safeCategory = /^[a-z]+$/.test(category || "") ? category : "other";
  return `${process.env.PUBLIC_URL || ""}/flex-icons/${safeCategory}-${iconVariantForProvenance(provenance)}.png`;
}
export const PALETTES = { ice: { focus: "#F5F5F0", break: "#9187AE" }, ember: { focus: "#FFAA55", break: "#9A90BE" }, mint: { focus: "#9DE0CB", break: "#A59ABD" } };
export function defaultLayout() {
  return { brand: { x: .07, y: .055, scale: 1, visible: true }, identity: { x: .07, y: .145, scale: 1, visible: true }, icon: { x: .07, y: .235, scale: 1, visible: true }, title: { x: .07, y: .365, scale: 1, visible: true }, metrics: { x: .07, y: .52, scale: 1, visible: true }, timeline: { x: .07, y: .72, scale: 1, visible: true } };
}
const SIZES = { brand: [.72, .07], identity: [.84, .07], icon: [.11, .11], title: [.84, .13], metrics: [.84, .16], timeline: [.84, .13] };
export function layerBounds(key, layer, width, height) {
  const [w, h] = SIZES[key];
  return { x: layer.x * width, y: layer.y * height, w: w * width * layer.scale, h: h * width * layer.scale };
}
export function constrainLayer(key, layer, format) {
  const [w, h] = SIZES[key], ratio = format === "story" ? 1920 / 1080 : 1;
  const scale = Math.max(.6, Math.min(1.05, Number(layer.scale) || 1));
  return { visible: layer.visible !== false, scale, x: Math.max(.035, Math.min(.965 - w * scale, Number(layer.x) || .07)), y: Math.max(.035, Math.min(.89 - h * scale / ratio, Number(layer.y) || .07)) };
}
function loadImage(src) {
  return new Promise((resolve) => {
    if (!src) return resolve(null);
    const img = new Image();
    const timeout = setTimeout(() => resolve(null), 8000);
    img.onload = () => { clearTimeout(timeout); resolve(img); };
    img.onerror = () => { clearTimeout(timeout); resolve(null); };
    img.src = src;
  });
}
export function durationLabel(seconds = 0) {
  const value = Math.max(0, Math.round(seconds)), hours = Math.floor(value / 3600), minutes = Math.floor((value % 3600) / 60);
  return hours ? `${hours}h ${minutes}m` : minutes ? `${minutes}m${value % 60 ? ` ${value % 60}s` : ""}` : `${value}s`;
}
function text(ctx, value, x, y, size, maxWidth, color = "#F5F5F0", weight = 500) {
  let fontSize = size;
  ctx.font = `${weight} ${fontSize}px Arial, sans-serif`;
  while (ctx.measureText(String(value)).width > maxWidth && fontSize > size * .58) { fontSize -= 1; ctx.font = `${weight} ${fontSize}px Arial, sans-serif`; }
  ctx.fillStyle = color; ctx.fillText(String(value), x, y, maxWidth);
}
export async function renderFlexCanvas(canvas, { stamp, username, timeZone = "UTC", format = "square", photo, transparent = false, layout = defaultLayout(), palette = "ice" }) {
  const width = 1080, height = format === "story" ? 1920 : 1080;
  canvas.width = width; canvas.height = height;
  const ctx = canvas.getContext("2d"), colors = PALETTES[palette] || PALETTES.ice;
  const category = /^[a-z]+$/.test(stamp.category || "") ? stamp.category : "other";
  const [logo, icon, background] = await Promise.all([
    loadImage(brandLogo),
    loadImage(flexCategoryIconUrl(category, stamp.provenance)).then((variant) =>
      variant || loadImage(`${process.env.PUBLIC_URL || ""}/flex-icons/${category}.png`)),
    loadImage(photo),
  ]);
  if (!transparent) {
    ctx.fillStyle = "#0B0D10"; ctx.fillRect(0, 0, width, height);
    if (background) {
      const scale = Math.max(width / background.width, height / background.height);
      ctx.drawImage(background, (width - background.width * scale) / 2, (height - background.height * scale) / 2, background.width * scale, background.height * scale);
      ctx.fillStyle = "rgba(0,0,0,.68)"; ctx.fillRect(0, 0, width, height);
    }
    const glow = ctx.createRadialGradient(width, 0, 0, width, 0, width * 1.2);
    glow.addColorStop(0, `${colors.focus}18`); glow.addColorStop(1, "#00000000"); ctx.fillStyle = glow; ctx.fillRect(0, 0, width, height);
  }
  ctx.textBaseline = "top";
  const bounds = {};
  for (const key of Object.keys(FLEX_LAYERS)) {
    const layer = constrainLayer(key, layout[key] || defaultLayout()[key], format);
    if (!layer.visible || (key === "timeline" && stamp.kind !== "focus")) continue;
    const box = layerBounds(key, layer, width, height); bounds[key] = box;
    const { x, y, w } = box, s = layer.scale;
    if (key === "brand") {
      if (logo) ctx.drawImage(logo, x, y, 56 * s, 56 * s);
      text(ctx, "HustlrMate", x + 72 * s, y + 6 * s, 36 * s, w - 72 * s, "#F5F5F0", 700);
    }
    if (key === "identity") {
      text(ctx, username ? `@${username}` : "My follow-through", x, y, 27 * s, w);
      let when = "Achievement earned";
      if (stamp.earnedAt) {
        try { when = new Intl.DateTimeFormat(undefined, { dateStyle: "medium", ...(stamp.dateOnly ? {} : { timeStyle: "short" }), timeZone }).format(new Date(stamp.earnedAt)); } catch { when = new Date(stamp.earnedAt).toLocaleDateString(); }
      }
      text(ctx, `${when}${stamp.dateOnly ? "" : ` · ${timeZone}`}`, x, y + 37 * s, 18 * s, w, "#BBBAC3");
    }
    if (key === "icon") {
      if (icon) ctx.drawImage(icon, x, y, w, w);
      else { ctx.strokeStyle = colors.focus; ctx.lineWidth = 2; ctx.beginPath(); ctx.arc(x + w / 2, y + w / 2, w * .44, 0, Math.PI * 2); ctx.stroke(); text(ctx, category === "focus" ? "◎" : category[0].toUpperCase(), x + w * .28, y + w * .22, w * .48, w * .65, colors.focus); }
    }
    if (key === "title") {
      text(ctx, stamp.kind === "focus" ? "STAYING FOCUSED IS A FLEX." : stamp.kind === "streak" ? "CONSISTENCY IS A FLEX." : "FOLLOW-THROUGH, RECOGNIZED.", x, y, 18 * s, w, colors.focus, 700);
      const title = String(stamp.title || "My follow-through");
      text(ctx, title.length > 80 ? `${title.slice(0, 77)}…` : title, x, y + 37 * s, 48 * s, w, "#F5F5F0", 700);
      if (stamp.kind === "streak") text(ctx, `${stamp.periodStart} → ${stamp.periodEnd}`, x, y + 94 * s, 18 * s, w, "#BBBAC3");
    }
    if (key === "metrics") {
      const rows = stamp.kind === "focus" ? [[durationLabel(stamp.focusedSeconds), "FOCUSED"], [durationLabel(stamp.breakSeconds), `${stamp.breakCount} BREAK${stamp.breakCount === 1 ? "" : "S"}`], [stamp.longestBlockSeconds == null ? `${stamp.extraMinutes || 0}m` : durationLabel(stamp.longestBlockSeconds), stamp.longestBlockSeconds == null ? "TIME ADDED" : "LONGEST BLOCK"]] : [[String(stamp.streakDays || stamp.completions || 1), stamp.kind === "streak" ? "DAY TASK STREAK" : stamp.kind === "award" ? "PARTNER AWARD" : "VERIFIED COMPLETIONS"]];
      rows.forEach(([value, label], i) => {
        const offset = x + i * w / rows.length, cellWidth = w / rows.length - 15 * s;
        text(ctx, value, offset, y, (rows.length === 1 ? 85 : 48) * s, cellWidth, "#F5F5F0", 700);
        text(ctx, label, offset, y + (rows.length === 1 ? 95 : 65) * s, 16 * s, cellWidth, "#BBBAC3");
      });
      if (stamp.kind === "focus") text(ctx, `+${stamp.extraMinutes || 0} min added · ${Math.round(100 * stamp.focusedSeconds / Math.max(1, stamp.focusedSeconds + stamp.breakSeconds))}% of recorded time in focus`, x, y + 111 * s, 18 * s, w, "#BBBAC3");
    }
    if (key === "timeline") {
      const total = Math.max(1, (stamp.timeline || []).reduce((n, item) => n + item.seconds, 0)); let cursor = x;
      (stamp.timeline || []).forEach((item) => { const length = item.seconds / total * w; ctx.fillStyle = item.kind === "focus" ? colors.focus : colors.break; ctx.fillRect(cursor, y + 30 * s, length, 34 * s); cursor += length; });
      text(ctx, stamp.timelineQuality === "recorded" ? "RECORDED FOCUS & BREAKS, IN ORDER" : "TIME SPLIT · COMPLETE INTERVAL HISTORY UNAVAILABLE", x, y, 15 * s, w, "#BBBAC3");
      text(ctx, "● Focus", x, y + 82 * s, 18 * s, w / 2, colors.focus); text(ctx, "● Break", x + w * .3, y + 82 * s, 18 * s, w / 2, colors.break);
    }
  }
  // Mandatory provenance stays outside the editor's movable area.
  ctx.fillStyle = "#14151C"; ctx.fillRect(38, height * .92, width - 76, 55);
  ctx.strokeStyle = ["verified", "awarded"].includes(stamp.provenance) ? colors.focus : "#777580";
  ctx.setLineDash(stamp.provenance === "solo" ? [6, 5] : []); ctx.lineWidth = 2; ctx.strokeRect(38, height * .92, width - 76, 55); ctx.setLineDash([]);
  text(ctx, PROVENANCE[stamp.provenance] || PROVENANCE.solo, 57, height * .92 + 18, 19, 610, "#F5F5F0", 700);
  text(ctx, "hustlrmate.in", width - 242, height * .92 + 18, 19, 185, "#BBBAC3");
  return bounds;
}
